import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  Col,
  Divider,
  Form,
  Input,
  Row,
  Skeleton,
  Select,
  Tooltip,
  Button,
  InputNumber,
} from 'antd';
import { useTranslation } from 'react-i18next';
import { FormInstance } from 'antd/lib/form';
import { validateCpfCnpj, maskPhone } from '@utils/validators';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { formatCpfCnpj } from '@utils/formatters';
import { ICustomer } from '@pages/Access/types';
import SessionContext from '@providers/Session/SessionContext';
import { InfoCircleOutlined } from '@ant-design/icons';
import ActionTypes from '@utils/deviceActions';

const { Item } = Form;

interface ICustomerInfoFormProps {
  isNewCustomer: boolean;
  form: FormInstance;
  onFormFinish: (values: any) => void;
  customerToUpdate?: ICustomer;
  isMotBusiness?: boolean;
  newApnData?: string[];
}

const CustomerInfoForm: React.FC<ICustomerInfoFormProps> = ({
  isNewCustomer,
  customerToUpdate,
  form,
  onFormFinish,
  isMotBusiness,
  newApnData,
}) => {
  const { t } = useTranslation(['customers', 'attributes', 'actions-modal']);
  const { keycloak } = useContext(KeycloakContext);
  const locale = keycloak?.idTokenParsed?.locale;
  const [apnData, setApnData] = useState<any>([]);
  const { isMotEnterprise } = useContext(SessionContext);

  const dueDayDefault = useMemo(() => {
    const { dueDay, secondDueDay } = customerToUpdate || {};

    if (dueDay && secondDueDay) {
      return [dueDay, secondDueDay];
    }

    if (dueDay) {
      return [dueDay];
    }

    return undefined;
  }, [customerToUpdate]);

  useEffect(() => {
    if (!isNewCustomer && customerToUpdate) {
      form.setFieldsValue({
        ...customerToUpdate,
        dueDay: dueDayDefault,
        apns: customerToUpdate?.apns?.map(apn => apn?.nameApn),
        actionUsages: {
          suspension: customerToUpdate?.actionUsages?.find(
            item => item.actionId === ActionTypes.SUSPENSION,
          )?.limit,
          cancellation: customerToUpdate?.actionUsages?.find(
            item => item.actionId === ActionTypes.BROKER_CANCELLATION,
          )?.limit,
        },
      });
    }
    if (newApnData) {
      setApnData(
        !isNewCustomer &&
          customerToUpdate &&
          customerToUpdate?.apns &&
          customerToUpdate?.apns.length > 0
          ? [
              ...customerToUpdate.apns,
              ...newApnData?.map((apn: string) => ({
                nameApn: apn,
                mainApn: false,
              })),
            ]
          : newApnData?.map((apn: string) => ({
              nameApn: apn,
              mainApn: false,
            })),
      );
    }
  }, [isNewCustomer, form, customerToUpdate, newApnData, dueDayDefault]);

  const handleFormFinish = useCallback(
    (formData: any) => {
      const parentId = keycloak.idTokenParsed?.customerId;
      const newFormData = {
        ...formData,
        type: 'CUSTOMER',
        parentId,
      };
      onFormFinish(newFormData);
    },
    [onFormFinish, keycloak.idTokenParsed?.customerId],
  );

  return (
    <Skeleton loading={!isNewCustomer && !customerToUpdate} active>
      <Form
        colon={false}
        form={form}
        scrollToFirstError
        onFinish={handleFormFinish}
        layout="vertical"
      >
        <Divider>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>
            {t('form.dividers.companyData')}
          </span>
        </Divider>
        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Name */}
            <Item
              label={t('attributes:companyName')}
              name="name"
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
              ]}
            >
              <Input disabled={!isNewCustomer && !isMotBusiness} allowClear />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Trading name */}
            <Item
              label={t('attributes:tradingName')}
              name="tradingName"
              rules={[]}
            >
              <Input allowClear />
            </Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col sm={8} xs={24}>
            {/* Nickname */}
            <Item label={t('attributes:nickname')} name="nickname" rules={[]}>
              <Input allowClear />
            </Item>
          </Col>
          <Col sm={8} xs={24}>
            {/* CPF/CNPJ */}
            <Item
              label={t('attributes:cpfCnpj')}
              name="cpfCnpj"
              normalize={value => formatCpfCnpj(value)}
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
                {
                  validator: (rule, value) => {
                    if (!value || validateCpfCnpj(value)) {
                      return Promise.resolve();
                    }
                    return Promise.reject(t('form.msgs.invalidCpfCnpj'));
                  },
                },
              ]}
            >
              <Input maxLength={18} allowClear />
            </Item>
          </Col>
          {newApnData && newApnData.length > 0 && (
            <Col sm={8} xs={24}>
              {/* APN */}
              <Item label={t('attributes:apns')} name="apns">
                <Select mode="multiple">
                  {apnData
                    ?.filter(
                      (apn: any, index: number, self: any[]) =>
                        index ===
                        self.findIndex((a: any) => a.nameApn === apn.nameApn),
                    )
                    ?.map((apn: any) => (
                      <Select.Option
                        key={apn.nameApn}
                        value={apn.nameApn}
                        disabled={apn.mainApn}
                      >
                        {apn.nameApn}
                      </Select.Option>
                    ))}
                </Select>
              </Item>
            </Col>
          )}
        </Row>

        <Divider>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>
            {t('form.dividers.contact')}
          </span>
        </Divider>

        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* Contact name */}
            <Item
              label={t('attributes:name')}
              name={['contact', 'name']}
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
              ]}
            >
              <Input allowClear />
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Contact phone number */}
            <Item
              label={t('attributes:phoneNumber')}
              name={['contact', 'phoneNumber']}
              normalize={value => maskPhone(value, locale)}
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
              ]}
            >
              <Input maxLength={15} allowClear />
            </Item>
          </Col>
        </Row>
        <Row gutter={[16, 16]}>
          <Col sm={8} xs={24}>
            {/* Email business */}
            <Item
              label={t('attributes:businessEmail')}
              name={['contact', 'email']}
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
                {
                  type: 'email',
                  message: t('form.msgs.notValidEmail'),
                },
              ]}
            >
              <Input allowClear />
            </Item>
          </Col>
          <Col sm={8} xs={24}>
            {/* Support email */}
            <Item
              label={t('attributes:supportEmail')}
              name={['contact', 'supportEmail']}
              rules={[
                {
                  required: true,
                  message: t('form.msgs.fillThisField'),
                },
                {
                  type: 'email',
                  message: t('form.msgs.notValidEmail'),
                },
              ]}
            >
              <Input allowClear />
            </Item>
          </Col>
          <Col sm={8} xs={24}>
            {/* Financial email */}
            <Item
              label={t('attributes:financialEmail')}
              name={['contact', 'financialEmail']}
              rules={[
                {
                  type: 'email',
                  message: t('form.msgs.notValidEmail'),
                },
              ]}
            >
              <Input allowClear />
            </Item>
          </Col>
        </Row>

        <Divider>
          <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>
            {t('form.dividers.address')}
          </span>
        </Divider>

        <Row gutter={[16, 16]}>
          <Col sm={12} xs={24}>
            {/* City and state */}
            <Item label={`${t('attributes:city')} / ${t('attributes:state')}`}>
              <Input.Group compact>
                <Item name={['address', 'city']} label="Cidade" noStyle>
                  <Input
                    autoComplete="address-level2"
                    maxLength={20}
                    style={{ width: '60%' }}
                    allowClear
                  />
                </Item>
                <Item name={['address', 'state']} noStyle>
                  <Input
                    autoComplete="address-level1"
                    maxLength={20}
                    style={{ width: '40%' }}
                    allowClear
                  />
                </Item>
              </Input.Group>
            </Item>
          </Col>
          <Col sm={12} xs={24}>
            {/* Street name and building number */}
            <Item
              label={`${t('attributes:streetName')} / ${t(
                'attributes:number',
              )}`}
            >
              <Input.Group compact>
                <Item name={['address', 'streetName']} noStyle>
                  <Input
                    autoComplete="address-line1"
                    maxLength={20}
                    style={{ width: '70%' }}
                    allowClear
                  />
                </Item>
                <Item
                  name={['address', 'number']}
                  normalize={value => value.replace(/\D/g, '')}
                  noStyle
                >
                  <Input
                    maxLength={20}
                    required={false}
                    style={{ width: '30%' }}
                    allowClear
                  />
                </Item>
              </Input.Group>
            </Item>
          </Col>
        </Row>

        <Row gutter={[16, 16]}>
          <Col sm={8} xs={24}>
            {/* Neighborhood */}
            <Item
              label={t('attributes:neighborhood')}
              name={['address', 'neighborhood']}
            >
              <Input autoComplete="address-level3" maxLength={20} allowClear />
            </Item>
          </Col>
          <Col sm={8} xs={24}>
            {/* Complement */}
            <Item
              label={t('attributes:complement')}
              name={['address', 'complement']}
            >
              <Input autoComplete="address-line2" maxLength={20} allowClear />
            </Item>
          </Col>
          <Col sm={8} xs={24}>
            {/* Country */}
            <Item label={t('attributes:country')} name={['address', 'country']}>
              <Input autoComplete="country-name" maxLength={20} allowClear />
            </Item>
          </Col>
        </Row>

        {isMotEnterprise && (
          <>
            <Divider>
              <span style={{ opacity: 0.6, fontSize: '0.9rem' }}>
                {t('form.dividers.billing')}
              </span>
            </Divider>
            <Row gutter={[16, 16]}>
              <Col xl={10} xs={24}>
                {/* Due day */}
                <Item
                  label={t('attributes:dueDay')}
                  name="dueDay"
                  rules={[
                    {
                      validator: (_, value) => {
                        if (value && value.length > 2) {
                          return Promise.reject(
                            new Error(t('form.msgs.dueDayLength')),
                          );
                        }
                        return Promise.resolve();
                      },
                    },
                  ]}
                >
                  <Select
                    mode="multiple"
                    options={[{ value: 15 }, { value: 20 }, { value: 25 }]}
                  />
                </Item>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <div style={{ width: '100%' }}>
                <Divider orientation="left">
                  <span style={{ opacity: '0.6' }}>
                    {t('actions-modal:actions-limit.monthly-actions-limit')}
                  </span>
                  <Tooltip
                    title={t(
                      'actions-modal:actions-limit.monthly-actions-limit-description',
                    )}
                  >
                    <Button type="link" icon={<InfoCircleOutlined />} />
                  </Tooltip>
                </Divider>
              </div>
              <Col sm={12} xs={24}>
                <Item
                  label={t(`device-actions:labels.${ActionTypes.SUSPENSION}`)}
                  name={['actionUsages', 'suspension']}
                >
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Item>
              </Col>
              <Col sm={12} xs={24}>
                <Item
                  label={t(
                    `device-actions:labels.${ActionTypes.BROKER_CANCELLATION}`,
                  )}
                  name={['actionUsages', 'cancellation']}
                >
                  <InputNumber
                    min={0}
                    step={1}
                    precision={0}
                    style={{ width: '100%' }}
                  />
                </Item>
              </Col>
            </Row>
          </>
        )}
      </Form>
    </Skeleton>
  );
};

export default CustomerInfoForm;
